import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from '@mui/material';
import TagPicker from 'rsuite/TagPicker';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import excel from 'src/Images/excel.svg';
import styles from './ModulesConfriguration.module.scss';
import { modalActions } from '../../../Actions';
import { notificationServices } from '../../../Services';
import userRolesAndPermissions from '../../../Services/userRolesAndPermissions.services';
import { navMenus } from '../../../Constants';
import { CreatePageHeader } from '../../../Components/Headers';
import modulesConfiguration from '../../../Services/modulesConfiguration.services';
import { BackdropLoader } from '../../../Components/Loaders';
import { downloadCSV } from '../../../Helpers';
import { useAuth } from '@/Components/Auth/Login/auth';
import SearchBar from '../../../Components/SearchBar/SearchBar';

const INTERNAL_ROLE = 'INTERNAL';

// Case-insensitive substring match of a module/page name against an already
// lower-cased, trimmed search text.
const nameMatches = (name, text) => { return String(name ?? '').toLowerCase().includes(text); };

// Wrap a value for CSV output: normalise non-breaking spaces (U+00A0) to plain
// spaces so names pasted from Word/web don't show as mojibake, then escape quotes.
const csvCell = (value) => `"${String(value ?? '').split(String.fromCharCode(0xA0)).join(' ').replace(/"/g, '""')}"`;

function ModulesConfiguration() {
	const [isloading, setIsloading] = useState(false);
	const [rolesMapping, setRolesMapping] = useState({});
	const [userRoles, setUserRoles] = useState([]);
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};

	useEffect(async () => {
		setIsloading(true);
		const userRolesRes = await userRolesAndPermissions.getUserRoles();
		setUserRoles(userRolesRes);
		const userRolesMappings =
			await modulesConfiguration.getModulesConfiguration();
		setRolesMapping(userRolesMappings.data);
		setIsloading(false);
	}, []);

	const handleSave = async () => {
		const req = {
			data: rolesMapping,
		};
		setIsloading(true);
		const updateData =
			await modulesConfiguration.updateModulesConfiguration(req);
		if (updateData.statusCode === 200) {
			notificationServices.generateNotification({
				type: 'success',
				message: updateData.message,
			});
		} else {
			notificationServices.generateNotification({
				type: 'error',
				message: updateData.message,
			});
		}
		setIsloading(false);
	};

	const handleExport = () => {
		// Map role id (stored in rolesMapped) -> human readable role name.
		const roleNameById = (userRoles ?? []).reduce((acc, role) => {
			acc[role.value] = role.label;
			return acc;
		}, {});

		// Flatten the nav menu into the same set of mappable pages the UI
		// renders: a parent with a submenu contributes its children (the parent
		// is the Parent Module, the child is the Module Page); a parent without
		// one contributes itself (Parent Module only, no Module Page).
		const pages = [];
		Object.values(navMenus).forEach((val) => {
			if (val?.type === 'NON_LINK' || !val?.name || !val?.id) {
				return;
			}
			const submenus = Object.values(val?.submenu ?? {}).filter(
				(subVal) => { return subVal?.id && subVal?.name; }
			);
			if (submenus.length) {
				submenus.forEach((subVal) => {
					pages.push({
						id: subVal.id,
						parentModule: val.name,
						modulePage: subVal.name,
					});
				});
			} else {
				pages.push({
					id: val.id,
					parentModule: val.name,
					modulePage: '',
				});
			}
		});

		// One row per (page, role); INTERNAL is excluded.
		const rows = [];
		pages.forEach((page) => {
			const mappedRoles = rolesMapping?.[page.id]?.rolesMapped ?? [];
			mappedRoles.forEach((roleId) => {
				const roleName = roleNameById?.[roleId];
				// Skip role ids that don't resolve to a known role (unmapped/stale
				// mappings) and INTERNAL — never emit a raw id into the export.
				if (!roleName || String(roleName).toUpperCase() === INTERNAL_ROLE) {
					return;
				}
				// When a parent module has no submenu, Module Page is empty — mirror
				// the Parent Module name into it so no row has a blank Module Page.
				rows.push([page.parentModule, page.modulePage || page.parentModule, roleName]);
			});
		});

		if (!rows.length) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'No roles mapped to export',
			});
			return;
		}

		const csv = [
			['Parent Module', 'Module Page', 'Role'],
			...rows,
		]
			.map((row) => { return row.map(csvCell).join(','); })
			.join('\n');

		downloadCSV(csv, 'Module Configuration.csv');
	};

	const [dropDown, setDropDown] = useState({});

	const handleClick = (id) => {
		const localDropDown = dropDown;
		if (!localDropDown[id]) {
			localDropDown[id] = true;
		} else localDropDown[id] = false;
		setDropDown({
			...localDropDown,
		});
	};

	const [query, setQuery] = useState('');
	const searchText = query.trim().toLowerCase();

	const handleSearch = (value) => {
		setQuery(value);
		const text = String(value ?? '').trim().toLowerCase();
		// Open every accordion holding a matching submenu so the hits are
		// visible; clearing the search collapses everything back.
		const expanded = {};
		if (text) {
			Object.entries(navMenus).forEach(([navKey, val]) => {
				const hasMatch = Object.values(val?.submenu ?? {}).some((subVal) => {
					return nameMatches(subVal?.name, text);
				});
				if (hasMatch) {
					expanded[navKey] = true;
				}
			});
		}
		setDropDown(expanded);
	};

	// Menus to render, narrowed by the search: a parent whose name matches keeps
	// all its submenus; otherwise only its matching submenus are kept, and the
	// parent is dropped when none match. Rows are keyed by the navMenus object
	// keys, not by id: several menus share an id (e.g. Process Flow and Process
	// Config are both 7), and duplicate React keys leave stale rows on screen
	// when the filtered list changes.
	const visibleMenus = Object.entries(navMenus).reduce((acc, [navKey, val]) => {
		if (val?.type === 'NON_LINK' || !val?.name || !val?.id) {
			return acc;
		}
		const submenus = Object.entries(val?.submenu ?? {}).filter(([, subVal]) => {
			return subVal?.id;
		});
		if (!searchText || nameMatches(val.name, searchText)) {
			acc.push({
				navKey,
				val,
				submenus,
			});
			return acc;
		}
		const matchedSubmenus = submenus.filter(([, subVal]) => {
			return nameMatches(subVal?.name, searchText);
		});
		if (matchedSubmenus.length) {
			acc.push({
				navKey,
				val,
				submenus: matchedSubmenus,
			});
		}
		return acc;
	}, []);

	return (
		<div className={styles.root}>
			<BackdropLoader showProgress isLoading={isloading} />
			<div
				style={{
					position: 'sticky',
					top: '50px',
					zIndex: 999,
				}}
			>
				<CreatePageHeader
					handleSave={handleSave}
					showCancel={false}
					heading='Module Configuration'
					renderBtns={() => {
						return (
							<div
								style={{
									display: 'flex',
									gap: '10px',
									alignItems: 'center',
								}}
							>
								<div
									style={{
										width: '18rem',
									}}
								>
									<SearchBar
										value={query}
										placeholder='Search modules or pages'
										handleInput={handleSearch}
									/>
								</div>
								<div
									onClick={handleExport}
									style={{
										height: '2.5rem',
										padding: '0 1rem',
										cursor: 'pointer',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: '0.5rem',
										backgroundColor: '#15803d',
										borderRadius: '6px',
									}}
								>
									<img
										src={excel}
										alt='excel'
										style={{
											width: '1.25rem',
										}}
									/>
									<span
										style={{
											color: '#fff',
											fontSize: '13px',
											fontWeight: 500,
										}}
									>
										EXPORT
									</span>
								</div>
								{level !== 1 && (
									<Button
										variant='contained'
										color='primary'
										onClick={handleSave}
									>
										Save
									</Button>
								)}
							</div>
						);
					}}
				/>
			</div>
			{!!searchText && !visibleMenus.length && (
				<div
					style={{
						padding: '20px',
						textAlign: 'center',
						color: '#64748b',
					}}
				>
					{`No modules or pages match "${query.trim()}"`}
				</div>
			)}
			{visibleMenus.map(({ navKey, val, submenus }) => {
				return (
					<div
						key={navKey}
						style={{
							backgroundColor: '#e2e8f0',
						}}
					>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								margin: '10px 10px 0 10px',
								minHeight: '50px',
							}}
							onClick={() => { return handleClick(navKey); }}
						>
							<div
								style={{
									fontWeight: '600',
									marginRight: '10px',
									display: 'flex',
									fontSize: '15px',
									alignItems: 'center',
								}}
							>
								{/* <div
									style={{
										marginRight: '10px',
									}}
								>
									{val.id}
									.
								</div> */}
								<div style={{
									textWrap: 'nowrap',
								}}
								>
									{val.name}
								</div>
							</div>
							{(!val?.submenu ||
								Object.keys(val.submenu)?.length === 0) && (
								<div
									style={{
										display: 'flex',
										margin: '5px',
										alignItems: 'center',
										width: '100%',
									}}
								>
									<TagPicker
										preventOverflow
										value={
											rolesMapping?.[val.id]
												?.rolesMapped
										}
										style={{
											minWidth: '100%',
										}}
										placeholder='Select To Map User Roles'
										data={userRoles}
										onChange={(changedVal) => {
											const roles = rolesMapping;
											roles[val.id] = {
												name: val.name,
												rolesMapped: changedVal,
												disable: val.disabled,
											};
											setRolesMapping({
												...roles,
											});
										}}
									/>
								</div>
							)}
							{!!val?.submenu && (
								<div>
									{!dropDown[navKey] ? (
										<ArrowDropDown sx={{
											fontSize: 30,
										}}
										/>
									) : (
										<ArrowDropUp sx={{
											fontSize: 30,
										}}
										/>
									)}
								</div>
							)}
						</div>
						{!!val?.submenu && (
							<div
								style={{
									backgroundColor: '#f1f5f9',
									maxHeight: dropDown[navKey] ? 'none' : '0',
									overflow: 'hidden',
									transition: 'max-height 1s ease',
								}}
							>
								{submenus.map(
									([subKey, subVal]) => {
										return (
											<div
												key={subKey}
												style={{
													display: 'flex',
													justifyContent:
														'space-between',
													marginLeft: '25px',
													marginRight: '20px',
												}}
											>
												<span
													style={{
														fontSize: '15px',
														fontWeight: '460',
														padding: '10px',
														display: 'flex',
													}}
												>
													{/* {`${subVal.id}. `} */}
													<div
														style={{
															marginLeft:
																'5px',
															textWrap: 'nowrap',
														}}
													>
														{subVal.name}
													</div>
												</span>
												<div
													style={{
														display: 'flex',
														margin: '0 5px',
														alignItems:
															'center',
														width: '100%',
													}}
												>
													<TagPicker
														preventOverflow
														value={
															rolesMapping?.[
																subVal.id
															]?.rolesMapped
														}
														style={{
															minWidth:
																'100%',
														}}
														onChange={(
															changedVal
														) => {
															const roles =
																rolesMapping;
															roles[
																subVal.id
															] = {
																rolesMapped:
																	changedVal,
																name: subVal.name,
																disable:
																	subVal.disabled,
															};
															setRolesMapping(
																{
																	...roles,
																}
															);
														}}
														placeholder='Select To Map User Roles'
														data={userRoles}
													/>
												</div>
											</div>
										);
									}
								)}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
const mapStateToProps = (state) => {
	const { userPermissions } = state.navigation;
	return {
		userPermissions,
	};
};

const mapDispatchToProps = {
	toggleModal: modalActions.toggleListModal,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ModulesConfiguration);
